import { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Error boundary specifically for analytics providers.
 * When an analytics SDK crashes (e.g., due to browser extension interference),
 * this boundary catches the error and renders the fallback (or nothing),
 * preventing the crash from taking down the entire app.
 *
 * IMPORTANT: Only wrap analytics providers with this — NOT the whole app.
 * Wrapping the entire app causes an infinite remount loop if any component
 * throws a persistent error.
 */
export class AnalyticsErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    // Mark as errored — stop rendering the failing analytics children
    return { hasError: true };
  }

  componentDidCatch(error: Error, _errorInfo: ErrorInfo) {
    console.warn('[AnalyticsErrorBoundary] Analytics error suppressed:', error.message);
    try {
      Sentry.captureException(error, {
        tags: { source: 'analytics_error_boundary' },
        level: 'warning',
      });
    } catch {
      // ignore
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Analytics failed — render fallback (nothing by default).
      // The rest of the app continues to work normally.
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}

export default AnalyticsErrorBoundary;
